import { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { YedekParcaData } from '../types/ServiceRequest';
import { useUser } from '../contexts/UserContext';
import { PLACEHOLDER_IMAGE } from '../constants/api';
import { FileService } from '../services/fileService';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { useModalBackHandler } from '../hooks/useModalBackHandler';
import { useRipple } from '../hooks/useRipple';

interface YedekParcaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (part: YedekParcaData) => void;
  onSearch: (term: string) => void;
  parts: YedekParcaData[];
  loading: boolean;
  searchTerm: string;
}

const YedekParcaModal: React.FC<YedekParcaModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  onSearch,
  parts,
  loading,
  searchTerm
}) => {
  // Ana modal için scroll engelleyici hook kullan
  useScrollPrevention(isOpen);
  
  const [searchTermInternal, setSearchTermInternal] = useState(searchTerm);
  const [mounted, setMounted] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});
  const [isCloseButtonPressed, setIsCloseButtonPressed] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const selectedPartRef = useRef<HTMLButtonElement | null>(null);
  const { userData } = useUser();
  
  // Ripple hook
  const { ripples, createRipple } = useRipple<number>();

  // Modal geri düğmesi handler - büyük görsel açıkken devre dışı
  const handleModalClose = useCallback(() => {
    // Büyük görsel açıksa önce onu kapat
    if (enlargedImage) {
      setEnlargedImage(null);
      return;
    }
    
    // Modal'ı kapat
    onClose();
  }, [onClose, enlargedImage]);
  
  useModalBackHandler({ 
    isOpen, 
    onClose: handleModalClose, 
    enabled: true
  });
  
  // Component mount olduğunda
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  
  // Search term güncellendiğinde internal değeri de güncelle
  useEffect(() => {
    if (isOpen) {
      setSearchTermInternal(searchTerm);
      // İlk yükleme durumunu kontrol et - loading false ise ve modal açıldıysa artık ilk yükleme değil
      if (parts.length > 0 || !loading) {
        setIsFirstLoad(false);
      }
    }
  }, [searchTerm, isOpen, parts.length, loading]);


  
  // Input değeri değiştiğinde arama yap (debounce ile)
  useEffect(() => {
    if (!isOpen) return;
    
    const timer = setTimeout(() => {
      // Değişiklik varsa arama yap (boş string dahil)
      if (searchTermInternal.trim() !== searchTerm.trim()) {
        setIsSearching(true);
        onSearch(searchTermInternal);
      }
    }, 500); // 500ms bekleyerek debounce uygula
    
    return () => clearTimeout(timer);
  }, [searchTermInternal, searchTerm, onSearch, isOpen]);
  
  // Modal içeriği değiştiğinde scroll'u en yukarı taşı
  useEffect(() => {
    if (isOpen) {
      const contentDiv = document.getElementById('yedek-parca-content');
      if (contentDiv) {
        contentDiv.scrollTop = 0;
      }
    }
  }, [parts, isOpen]);

  // Modal açıklık durumu değiştiğinde
  useEffect(() => {
    if (!isOpen) {
      // Modal kapandığında yüklenen görselleri sıfırla
      setLoadedImages({});
      setIsFirstLoad(true);
      setIsSearching(false);
    } else {
      // Modal açıldığında ilk yükleme durumunu kontrol et
      // Eğer loading false ise (veriler zaten yüklü) ilk yükleme değil
      if (!loading) {
        setIsFirstLoad(false);
      }
    }
  }, [isOpen, loading]);
  
  // Parts değiştiğinde searching durumunu kapat
  useEffect(() => {
    if (isSearching) {
      setIsSearching(false);
    }
  }, [parts]);



  // Input değişikliğini işle
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTermInternal(e.target.value);
  };

  // Görsel yükleme işleyicisi
  const handleImageLoad = useCallback((imageId: string) => {
    setLoadedImages(prev => ({
      ...prev,
      [imageId]: true
    }));
  }, []);

  // Görsele tıklama işleme
  const handleImageClick = (e: React.MouseEvent, imageId: string | null) => {
    e.stopPropagation(); // Event'in parent elemanlara iletilmesini engelle
    
    if (!imageId) return;
    
    setEnlargedImage(imageId);
  };

  // Büyük görsel modalını kapat
  const closeEnlargedImage = () => {
    setEnlargedImage(null);
  };

  // Stok durumunu kullanıcı ayarlarına göre göster
  const renderStockInfo = (part: YedekParcaData) => {
    // ShowStock false ise sadece var/yok göster
    if (userData && !userData.ShowStock) {
      return (
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${
          part.Stok > 0
            ? 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700'
            : 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700'
        }`}>
          {part.Stok > 0 ? 'Var' : 'Yok'}
        </span>
      );
    }
    
    // ShowStock true ise stok miktarını göster
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${
        part.Stok > 0
          ? 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700'
          : 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700'
      }`}>
        {part.Stok > 0 ? `Stok: ${part.Stok}` : 'Stokta Yok'}
      </span>
    );
  };

  // Modal içeriği
  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="fixed inset-0 z-50 flex flex-col justify-end md:justify-center bg-black/20 dark:bg-black/40 backdrop-blur-sm modal-backdrop-optimized"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ 
              type: 'tween', 
              ease: [0.32, 0.72, 0, 1], // Custom easing for better performance
              duration: 0.25,
              opacity: { duration: 0.2 }
            }}
            className="w-full max-w-md mx-auto border border-gray-200 dark:border-gray-700 rounded-t-2xl md:rounded-2xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col max-h-[70vh] md:max-h-[75vh] modal-optimized"
            style={{ 
              minHeight: 328,
              willChange: 'transform, opacity',
              transform: 'translateZ(0)' // Force GPU acceleration
            }}
            onClick={e => e.stopPropagation()}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.2}
            onDragEnd={(event, info) => {
              // Sadece aşağıya doğru 150px'den fazla kaydırıldığında kapat
              if (info.offset.y > 150) {
                onClose();
              }
            }}
          >
            <div className="rounded-t-2xl md:rounded-2xl p-4 pt-6 border border-white dark:border-gray-700 flex flex-col h-full">
            {/* Modal başlık/tutamaç */}
            <div className="flex justify-center mb-4 flex-shrink-0 cursor-grab active:cursor-grabbing">
              <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>
            
            {/* Arama kutusu */}
            <div className="mb-4 flex-shrink-0">
              <div className="relative">
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700">
                  <input
                    type="text"
                    className="w-full pr-10 bg-transparent border border-white dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Parça adını ara..."
                    value={searchTermInternal}
                    onChange={handleInputChange}
                    disabled={loading && isFirstLoad}
                  />
                </div>
                {/* Loading indicator veya temizle butonu */}
                <div className="absolute right-3 flex items-center" style={{ top: 0, bottom: 0 }}>
                  {isSearching ? (
                    <svg className="animate-spin w-5 h-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : searchTermInternal ? (
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none"
                      onClick={() => {
                        setSearchTermInternal('');
                        setIsSearching(true);
                        onSearch('');
                      }}
                      tabIndex={-1}
                      aria-label="Temizle"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
            
            {/* Parça listesi */}
            <div 
              id="yedek-parca-content"
              className="overflow-y-auto flex-grow flex flex-col"
              data-modal-scrollable
              onPointerDownCapture={(e) => e.stopPropagation()}
            >
              {loading && isFirstLoad ? (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 flex-1 min-h-[200px]">
                  <div className="rounded-lg border border-white dark:border-gray-700 flex items-center justify-center h-full">
                  <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                </div>
              ) : parts.length === 0 ? (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700">
                  <div className="rounded-lg border border-white dark:border-gray-700 py-8 flex flex-col justify-center items-center text-center">
                  <div className="bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 dark:from-gray-600 dark:via-gray-600/90 dark:to-gray-500 border border-gray-200 dark:border-gray-500 rounded-full p-4 mb-4">
                    <svg className="w-16 h-16 text-gray-400 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">
                    Uygun parça bulunamadı.
                  </p>
                  {searchTermInternal && (
                    <button
                      className="mt-4 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-semibold"
                      onClick={() => {
                        setSearchTermInternal('');
                        setIsSearching(true);
                        onSearch('');
                      }}
                    >
                      Aramayı Temizle
                    </button>
                  )}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {parts.map(part => (
                    <button
                      key={part.Id}
                      type="button"
                      ref={selectedPartRef}
                      className="relative overflow-hidden w-full rounded-lg transition-all duration-200 border border-gray-200 dark:border-gray-600 bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-indigo-50 hover:via-blue-50 hover:to-indigo-100 dark:hover:from-indigo-900/30 dark:hover:via-blue-900/25 dark:hover:to-indigo-800/30 hover:border-indigo-200 dark:hover:border-indigo-700 shadow-sm hover:shadow-md"
                      onClick={(e) => createRipple(e, part.Id, () => onSelect(part), 100)}
                    >
                      <div className="rounded-lg border border-white dark:border-gray-700 px-4 py-3 flex items-center text-left">
                      {/* Görsel varsa göster, yoksa yer tutucu kullan */}
                      <div className="flex-shrink-0 mr-3">
                        <div 
                          className={`w-12 h-12 flex items-center justify-center rounded-md ${part.Gorsel ? 'p-1 bg-white dark:bg-gray-700 cursor-pointer' : 'bg-slate-50 dark:bg-slate-800'} overflow-hidden`}
                          onClick={part.Gorsel ? (e) => handleImageClick(e, part.Gorsel) : undefined}
                        >
                          {part.Gorsel ? (
                            <div className="relative w-full h-full">
                              {!loadedImages[part.Gorsel] && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                                  <svg className="animate-spin h-4 w-4 text-indigo-600 opacity-75" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </div>
                              )}
                              <img 
                                src={FileService.getThumbnailUrl(part.Gorsel)} 
                                alt={part.Ad} 
                                className="w-full h-full object-contain"
                                loading="lazy"
                                onLoad={() => handleImageLoad(part.Gorsel as string)}
                                onError={(e) => {
                                  // Görsel yüklenemezse yer tutucu göster
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  e.currentTarget.parentElement?.parentElement?.classList.add('placeholder-image');
                                  e.currentTarget.parentElement?.parentElement?.classList.remove('p-1', 'bg-white', 'dark:bg-gray-700', 'cursor-pointer');
                                  e.currentTarget.parentElement?.parentElement?.classList.add('bg-slate-50', 'dark:bg-slate-800');
                                  const placeholder = document.createElement('img');
                                  placeholder.src = PLACEHOLDER_IMAGE;
                                  placeholder.className = 'w-6 h-6 opacity-60';
                                  e.currentTarget.parentElement?.parentElement?.appendChild(placeholder);
                                  
                                  // Hatalı görselleri yüklenmiş olarak işaretle
                                  handleImageLoad(part.Gorsel as string);
                                }}
                              />
                            </div>
                          ) : (
                            <img 
                              src={PLACEHOLDER_IMAGE} 
                              alt="Görsel mevcut değil" 
                              className="w-6 h-6 opacity-60" 
                            />
                          )}
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center mb-1">
                          <span className="font-medium text-gray-900 dark:text-white truncate">
                            {part.Ad}
                          </span>
                        </div>
                        <div className="flex items-center text-sm">
                          <div className="flex-1 truncate">
                            <span className="text-gray-500 dark:text-gray-400">
                              Kod: {part.Kod}
                            </span>
                          </div>
                          <div className="min-w-[80px]">
                            {renderStockInfo(part)}
                          </div>
                        </div>
                      </div>
                        <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400 ml-4 flex-shrink-0 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      
                      {/* Ripple Effects */}
                      {ripples[part.Id]?.map((ripple) => (
                        <span
                          key={ripple.id}
                          className="absolute rounded-full pointer-events-none bg-indigo-200/60 dark:bg-indigo-400/40"
                          style={{
                            left: ripple.x - (ripple.size || 0) / 2,
                            top: ripple.y - (ripple.size || 0) / 2,
                            width: ripple.size || 0,
                            height: ripple.size || 0,
                            animation: 'ripple-modal 600ms ease-out forwards'
                          }}
                        />
                      ))}
                    </button>
                  ))}
                </div>
              )}
            </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Büyük görsel modalı */}
      {enlargedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          onClick={closeEnlargedImage}
        >
          <div className="relative max-w-full max-h-full overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-1">
            {!loadedImages[`large_${enlargedImage}`] && (
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="animate-spin h-10 w-10 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
            <img
              src={FileService.getDownloadUrl(enlargedImage)}
              alt="Büyük görsel"
              className="max-w-full max-h-[90vh] object-contain rounded-xl"
              loading="eager" 
              onLoad={() => handleImageLoad(`large_${enlargedImage}`)}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = PLACEHOLDER_IMAGE;
                target.classList.add('w-16', 'h-16', 'opacity-60', 'p-8');
                handleImageLoad(`large_${enlargedImage}`);
              }}
            />
            <div className="group absolute top-2 right-2">
              <button
                type="button"
                style={{ 
                  WebkitTapHighlightColor: 'transparent',
                  transform: isCloseButtonPressed ? 'scale(0.9)' : 'scale(1)',
                  transition: 'transform 0.2s ease'
                }}
                className={`bg-white dark:bg-gray-700 rounded-full p-1 shadow-lg hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none ${isCloseButtonPressed ? '' : 'group-hover:scale-110'}`}
                onClick={closeEnlargedImage}
                onMouseDown={() => setIsCloseButtonPressed(true)}
                onMouseUp={() => setIsCloseButtonPressed(false)}
                onMouseLeave={() => setIsCloseButtonPressed(false)}
                onTouchStart={() => setIsCloseButtonPressed(true)}
                onTouchEnd={() => setIsCloseButtonPressed(false)}
                aria-label="Kapat"
              >
                <svg className={`w-6 h-6 text-gray-600 dark:text-gray-300 transition-transform duration-300 [@media(hover:hover)]:group-hover:rotate-90`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Component mount olmadıysa veya modal açık değilse null dön
  if (!mounted || !isOpen) return null;

  // Portal aracılığıyla modalı doğrudan body'ye render et
  return ReactDOM.createPortal(modalContent, document.body);
};

export default YedekParcaModal; 